import { Controller } from "@hotwired/stimulus"

const STORAGE_PREFIX = "data-table-widths:"

export default class extends Controller {
  static targets = ["header", "handle"]

  static values = {
    storageKey: String,
    minWidth: { type: Number, default: 60 }
  }

  connect() {
    if (!this.hasHeaderTarget) return

    const saved = this.loadWidths()

    this.headerTargets.forEach(header => {
      const key = header.dataset.columnKey
      const width = (key && saved[key]) || header.offsetWidth
      header.style.width = `${width}px`
    })

    const table = this.element.querySelector("table")
    if (table) table.style.tableLayout = "fixed"

    this.element.classList.add("is-resizable")
  }

  startResize(event) {
    event.preventDefault()
    event.stopPropagation()

    const handle = event.currentTarget
    const header = handle.closest("th")
    if (!header) return

    this.activeHeader = header
    this.startX = event.clientX
    this.startWidth = header.offsetWidth

    if (handle.setPointerCapture) {
      try { handle.setPointerCapture(event.pointerId) } catch (_) { /* noop */ }
    }

    document.body.classList.add("data-table-resizing")
  }

  doResize(event) {
    if (!this.activeHeader) return

    const delta = event.clientX - this.startX
    const newWidth = Math.max(this.minWidthValue, this.startWidth + delta)
    this.activeHeader.style.width = `${newWidth}px`
  }

  stopResize(event) {
    if (!this.activeHeader) return

    const handle = event.currentTarget
    if (handle && handle.releasePointerCapture) {
      try { handle.releasePointerCapture(event.pointerId) } catch (_) { /* noop */ }
    }

    document.body.classList.remove("data-table-resizing")
    this.persistCurrentWidths()
    this.activeHeader = null
  }

  resetColumn(event) {
    event.preventDefault()
    event.stopPropagation()

    const header = event.currentTarget.closest("th")
    if (!header) return

    header.style.width = ""
    const saved = this.loadWidths()
    delete saved[header.dataset.columnKey]
    this.saveWidths(saved)
  }

  persistCurrentWidths() {
    const map = {}
    this.headerTargets.forEach(header => {
      const key = header.dataset.columnKey
      if (!key) return
      const width = header.style.width
      if (width) map[key] = parseInt(width, 10)
    })
    this.saveWidths(map)
  }

  loadWidths() {
    if (!this.storageKeyValue) return {}
    try {
      const raw = localStorage.getItem(`${STORAGE_PREFIX}${this.storageKeyValue}`)
      return raw ? JSON.parse(raw) : {}
    } catch (_) {
      return {}
    }
  }

  saveWidths(map) {
    if (!this.storageKeyValue) return
    try {
      localStorage.setItem(`${STORAGE_PREFIX}${this.storageKeyValue}`, JSON.stringify(map))
    } catch (_) { /* noop */ }
  }
}
